import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Plane, UserMinus, Clock,
  ChevronLeft, ChevronRight, Inbox, X, Loader2, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import RequestModal from '../components/RequestModal';
import VacationEditModal from '../components/VacationEditModal';
import { useToast } from '../components/Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import {
  createEvent, getEventsByRange, uploadDocument, listAgreements,
  EVENT_TYPES, APPROVAL_STATUS,
} from '../services/api';
import { useHolidays } from '../hooks/useHolidays';
import { describeHolidays, isCompanyHoliday, COMPANY_HOLIDAY_LABEL } from '../lib/spainHolidays';
import { useWorkerSchedule } from '../hooks/useWorkerHourBalance';
import { makeSlotResolver } from '../lib/timeline';
import { isRestDayAt } from '../lib/scheduleHistory';
import { splitDaysByToday, yearCountWindow, countDaysInRange } from '../lib/vacationCounter';
import {
  buildDayStatusSets, resolveDayStatus, DAY_STATUS,
  countPeriodsByStatus, vacationHolidayTypeFor, SICK_LEAVE_HOLIDAY_TYPE,
} from '../lib/dayStatus';
import {
  timeZoneForWorker, timeZoneLabel, dayKeyInZone, formatHmInZone, minutesOfDayInZone,
  todayInZone, dayKeysBetween, zonedDateTimeToInstant,
} from '../lib/workerTime';

const SIDEBAR_ACTIONS = [
  { key: 'vacaciones', label: 'Solicitar vacaciones',          icon: Plane,       color: 'text-accent-dark bg-accent-light', supported: true },
  { key: 'baja',       label: 'Informar día completo',           icon: UserMinus, color: 'text-danger bg-danger/10',          supported: true },
  { key: 'ausencia',   label: 'Solicitar por horas',           icon: Clock,       color: 'text-warning bg-warning/10',        supported: true },
];

const RANGE_ACTIONS = [
  { key: 'vacaciones', label: 'Vacaciones', icon: Plane,       cls: 'bg-accent text-white hover:bg-accent-dark' },
  { key: 'baja',       label: 'Día completo', icon: UserMinus, cls: 'bg-danger text-white hover:bg-red-700' },
  // Ausencia: solo aparece como acción de rango cuando la selección abarca
  // UN único día (un evento por horas no tiene sentido sobre varios días).
  { key: 'ausencia',   label: 'Por horas',  icon: Clock,       cls: 'bg-warning text-white hover:bg-amber-700', singleDayOnly: true },
];

const TIPO_LABEL = {
  vacaciones: 'Vacaciones',
  baja: 'Día completo',
  ausencia: 'Por horas',
};

const TIPO_START_EVENT = {
  vacaciones: EVENT_TYPES.VACATION_START,
  baja: EVENT_TYPES.SICK_LEAVE_START,
  ausencia: EVENT_TYPES.ABSENCE_START,
};
const TIPO_END_EVENT = {
  vacaciones: EVENT_TYPES.VACATION_END,
  baja: EVENT_TYPES.SICK_LEAVE_END,
  ausencia: EVENT_TYPES.ABSENCE_END,
};

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DOW = ['L','M','X','J','V','S','D'];

// Solo para las celdas de la cuadrícula (Date construido con año/mes/día =
// etiqueta de calendario). Los INSTANTES de los eventos van con dayKeyInZone.
const isoLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Rango de claves de día (aritmética pura, sin zona del navegador).
const datesBetween = (startIso, endIso) => dayKeysBetween(startIso, endIso);

const buildMonthCells = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // firstWeekday: offset visual con cuadrícula que empieza en Lunes
  // (Lun=0, Mar=1, …, Dom=6). NO confundir con el dayOfWeek que usa el
  // backend (= Date.getDay() de JS: Dom=0, Lun=1, …, Sáb=6).
  const firstWeekday = (firstDay.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push({ empty: true });
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d);
    // dow se guarda en convención backend (Date.getDay()) para que el
    // lookup `workhoursByDow.get(dow)` matchee los slots tal como los
    // grabó la API. Antes guardábamos (getDay()+6)%7 (Monday-first) y
    // los días de descanso quedaban corridos: con jornada L-V, lunes y
    // domingo aparecían en gris y martes-sábado en blanco.
    cells.push({ day: d, iso: isoLocal(date), dow: date.getDay() });
  }
  while (cells.length % 7 !== 0) cells.push({ empty: true });
  return cells;
};

const orderedRange = (a, b) => (a <= b ? [a, b] : [b, a]);

// El emparejado de periodos (START/END) y los sets de estado por día viven en
// src/lib/dayStatus.js (ARCHIVO COMPARTIDO con la app): los usan también
// CompararCalendariosPage y el CalendarioScreen de la app, para que el mismo
// día se vea IGUAL en todas las pantallas.

const CalendarioPage = () => {
  const { user } = useAuth();
  // Zona del trabajador: el día de cada evento (y "hoy") se decide en SU hora
  // (Canarias / península), no en la del navegador.
  const tz = useMemo(
    () => timeZoneForWorker(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.autonomousCommunity, user?.province]
  );
  const tzHint = timeZoneLabel(tz);
  const todayKey = todayInZone(tz);
  const [viewYear, setViewYear] = useState(() => Number(todayKey.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(() => Number(todayKey.slice(5, 7)) - 1);

  const [modalTipo, setModalTipo] = useState(null);
  const [modalRange, setModalRange] = useState(null);
  // Modal de edición de un periodo de vacaciones existente. Lo abrimos al
  // hacer click en una celda que ya pertenece a un periodo de vacaciones
  // del propio trabajador (aprobadas o pendientes).
  const [editVacationPeriod, setEditVacationPeriod] = useState(null);

  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  // Convenio del trabajador para conocer su `vacationDays` (total anual) y
  // su `holidayType` ('laborables' | 'festivos'). Sin convenio asignado,
  // el resumen de vacaciones queda oculto.
  const [workerAgreement, setWorkerAgreement] = useState(null);

  // Ventana del año visible: los contadores cuentan solo los días de
  // [yearFrom, yearTo], pero los eventos se piden con margen (yearCountWindow)
  // para no perder los periodos que cruzan el cambio de año.
  const yearWindow = useMemo(() => yearCountWindow(viewYear), [viewYear]);
  // Festivos del año visible y del anterior: el arrastre de un periodo que
  // empieza en diciembre puede entrar en el año visible.
  const holidayYears = useMemo(() => [viewYear - 1, viewYear], [viewYear]);
  const { holidays, allByDate: holidaysByDate, community: communityName } = useHolidays(user, holidayYears);
  // Festivos del mes visible (oficiales y de empresa) para el listado bajo la
  // leyenda: así se ve el NOMBRE y de dónde viene cada uno.
  const monthHolidays = useMemo(() => {
    const prefix = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-`;
    const out = [];
    for (const [iso, entries] of holidaysByDate) {
      if (iso.startsWith(prefix)) out.push(...entries);
    }
    return out.sort((a, b) => a.date.localeCompare(b.date));
  }, [holidaysByDate, viewYear, viewMonth]);

  // Horario del propio usuario POR FECHA: histórico de versiones enriquecido
  // o, sin histórico, el horario actual (Map<dow, slot>). Mismo helper y
  // mismo fallback que la ficha del trabajador y que la app (src/lib/
  // scheduleHistory.js): antes el calendario usaba solo el horario actual y
  // sus contadores de vacaciones no cuadraban con los de la ficha.
  const { scheduleByDate, loading: scheduleLoading } = useWorkerSchedule(user?.id);
  const resolveSlot = useMemo(() => makeSlotResolver(scheduleByDate, tz), [scheduleByDate, tz]);

  // Carga el convenio asignado al trabajador (si tiene uno). Sin convenio
  // o sin agreement válido, no mostramos el resumen de vacaciones.
  useEffect(() => {
    if (!user?.agreement) { setWorkerAgreement(null); return; }
    let cancelled = false;
    listAgreements()
      .then((list) => {
        if (cancelled) return;
        const ag = (Array.isArray(list) ? list : [])
          .find((a) => Number(a.id) === Number(user.agreement)) || null;
        setWorkerAgreement(ag);
      })
      .catch(() => { if (!cancelled) setWorkerAgreement(null); });
    return () => { cancelled = true; };
  }, [user?.agreement]);

  const trim5 = (v) => v ? String(v).slice(0, 5) : '';

  // Eventos del día indexados por iso (para comparar con los slots).
  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      const iso = dayKeyInZone(e.timestamp, tz);
      if (!iso) continue;
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso).push(e);
    }
    return map;
  }, [events, tz]);

  // Indicador de turnos COMPLETADOS en un día concreto.
  // - Si el día es descanso (sin slot en workhours) → no hay puntos.
  // - Si hay slot, comparamos el último CLOCK_OUT con out1 / out2:
  //     last >= out1 → mañana completada (punto ámbar)
  //     last >= out2 → tarde completada  (punto índigo)
  //     last <  out1 (o sin clock-out)  → ningún turno cerrado, sin puntos
  // Esto refleja la realidad del fichaje, no el horario teórico.
  const dayShiftCompleted = (iso) => {
    const slot = resolveSlot(iso);
    if (!slot) return { kind: 'descanso', summary: 'Día sin jornada (descanso)' };

    const dayEvents = eventsByDay.get(iso) || [];
    const clockOuts = dayEvents
      .filter((e) => e.eventType === EVENT_TYPES.CLOCK_OUT)
      .map((e) => new Date(e.timestamp));
    // Hora de reloj del último cierre en la zona del trabajador (su horario
    // está en su hora).
    const lastOutMins = clockOuts.length
      ? minutesOfDayInZone(Math.max(...clockOuts.map((d) => d.getTime())), tz)
      : null;

    const parseHM = (hms) => {
      if (!hms) return null;
      const [h, m] = String(hms).slice(0, 5).split(':').map(Number);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };
    const out1 = parseHM(slot.out1);
    const out2 = parseHM(slot.out2);
    const m = trim5(slot.in1) && trim5(slot.out1) ? `${trim5(slot.in1)}–${trim5(slot.out1)}` : '';
    const t = trim5(slot.in2) && trim5(slot.out2) ? `${trim5(slot.in2)}–${trim5(slot.out2)}` : '';
    const summaryHorario = [m && `Mañana ${m}`, t && `Tarde ${t}`].filter(Boolean).join(' · ');

    const mananaCompletada = lastOutMins != null && out1 != null && lastOutMins >= out1;
    const tardeCompletada  = lastOutMins != null && out2 != null && lastOutMins >= out2;

    if (mananaCompletada && tardeCompletada) return { kind: 'partido', summary: `Completado · ${summaryHorario}` };
    if (mananaCompletada) return { kind: 'manana', summary: `Mañana completada · ${summaryHorario}` };
    if (tardeCompletada)  return { kind: 'tarde',  summary: `Tarde completada · ${summaryHorario}` };
    if (clockOuts.length) return { kind: 'incompleto', summary: `Jornada incompleta · ${summaryHorario}` };
    return { kind: 'pendiente', summary: `Sin cierre todavía · ${summaryHorario}` };
  };

  const loadEvents = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setError(null);
    // Pedimos el año visible con MARGEN (yearCountWindow, ±60 días): un
    // periodo 28-dic → 3-ene tiene el START en un año y el END en otro; sin
    // margen se perdía uno de los extremos y el periodo contaba 1 día en un
    // año y 0 en el otro. Los días fuera del año visible no se pintan (la
    // cuadrícula es solo del mes) y los contadores recortan al año (from/to).
    try {
      const data = await getEventsByRange(user.id, yearWindow.fetchFrom, yearWindow.fetchTo);
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, yearWindow]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    const stop = () => setIsDragging(false);
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const cells = useMemo(() => buildMonthCells(viewYear, viewMonth), [viewYear, viewMonth]);

  // Sets de estado por día (vacaciones / día completo / por horas aprobados y
  // pendientes, compensación, trabajado aprobado y pendiente) + periodos
  // emparejados. Los construye el helper compartido con la comparación de
  // calendarios. Recordatorio de la semántica:
  //   · Fichajes con approvalStatus null/undefined cuentan como APROBADOS
  //     (los fichajes "en directo" creados sin el campo quedan así); los
  //     PENDIENTES (correcciones) se pintan aparte, con borde discontinuo.
  //   · Periodos: "pendiente" = ni aprobado ni rechazado.
  const statusSets = useMemo(() => buildDayStatusSets(events, tz), [events, tz]);
  const {
    vacationPeriods, sickPeriods, absencePeriods,
    compensation: compensationSet,
    worked: workedSet,
  } = statusSets;

  // Cómputo de días de vacaciones y de "día completo" del año visible. TODO
  // lo que se muestra (resumen de vacaciones y bloque Balance) sale de aquí,
  // con la MISMA regla que la app (src/lib/dayStatus.js → countPeriodsByStatus):
  //   - Vacaciones → `holidayType` del convenio ('laborables' por defecto:
  //     solo días laborables del trabajador, sin festivos; 'festivos' =
  //     naturales con arrastre tras el fin del rango).
  //   - Día completo → siempre naturales (regla INSS, igual que la ficha).
  //   - Rechazados → no cuentan ni como aprobados ni como pendientes.
  // Antes el Balance contaba días de calendario en bruto y no cuadraba con
  // el resumen del convenio.
  //   - Solo cuentan los días DENTRO del año visible (from/to): un periodo que
  //     cruza el cambio de año reparte sus días entre los dos años.
  //   - Sin horario (cargando / sin configurar) la cifra no es fiable
  //     (`scheduleUnknown`): se pinta "—" en vez de un número inflado.
  const vacationHolidayType = vacationHolidayTypeFor(workerAgreement);
  const vacationCounts = useMemo(
    () => countPeriodsByStatus(vacationPeriods, {
      holidayType: vacationHolidayType, workHoursByDow: scheduleByDate, holidaysSet: holidays, timeZone: tz,
      from: yearWindow.from, to: yearWindow.to,
    }),
    [vacationPeriods, vacationHolidayType, scheduleByDate, holidays, tz, yearWindow]
  );
  const sickCounts = useMemo(
    () => countPeriodsByStatus(sickPeriods, {
      holidayType: SICK_LEAVE_HOLIDAY_TYPE, workHoursByDow: scheduleByDate, holidaysSet: holidays, timeZone: tz,
      from: yearWindow.from, to: yearWindow.to,
    }),
    [sickPeriods, scheduleByDate, holidays, tz, yearWindow]
  );
  const vacationCountsReady = !scheduleLoading && !vacationCounts.scheduleUnknown;
  const sickCountsReady = !scheduleLoading && !sickCounts.scheduleUnknown;
  const workedDaysInYear = useMemo(
    () => countDaysInRange(workedSet, yearWindow.from, yearWindow.to),
    [workedSet, yearWindow]
  );
  // Los días APROBADOS se parten en DISFRUTADOS (ya transcurridos) y
  // PROGRAMADOS (aprobados pero futuros). Ambos descuentan del cupo, pero solo
  // los primeros están realmente gastados: unas vacaciones concedidas para
  // agosto no son "disfrutadas" cuando las miras en julio.
  const vacationsApproved = useMemo(() => {
    const { pastCount, futureCount } = splitDaysByToday(vacationCounts.approved.days, todayInZone(tz), tz);
    return { total: vacationCounts.approved.count, taken: pastCount, scheduled: futureCount };
  }, [vacationCounts, tz]);
  const vacationsApprovedDays = vacationsApproved.total;
  const vacationsTakenDays = vacationsApproved.taken;
  const vacationsScheduledDays = vacationsApproved.scheduled;
  const vacationsPendingDays = vacationCounts.pending.count;
  const vacationsTotalDays = Number(workerAgreement?.vacationDays) || 0;
  const vacationsRemainingDays = Math.max(0, vacationsTotalDays - vacationsApprovedDays - vacationsPendingDays);
  const vacationsOverCap = vacationsApprovedDays + vacationsPendingDays > vacationsTotalDays;
  const showVacationSummary = !!workerAgreement && vacationsTotalDays > 0;

  const requestsList = useMemo(() => {
    const statusLabel = (s) => {
      if (s === APPROVAL_STATUS.APPROVED) return 'aprobada';
      if (s === APPROVAL_STATUS.REJECTED) return 'rechazada';
      return 'pendiente';
    };
    // Los eventos llegan con margen de año: solo se listan los periodos que
    // tocan el año visible.
    const inYear = (p) => {
      const s = dayKeyInZone(p.start, tz);
      const e = dayKeyInZone(p.end, tz);
      return !!s && !!e && e >= yearWindow.from && s <= yearWindow.to;
    };
    const out = [];
    vacationPeriods.filter(inYear).forEach((p, i) => out.push({
      id: `vac-${p.startId || i}`,
      tipo: 'vacaciones',
      fechaInicio: dayKeyInZone(p.start, tz),
      fechaFin: dayKeyInZone(p.end, tz),
      estado: statusLabel(p.approvalStatus),
    }));
    sickPeriods.filter(inYear).forEach((p, i) => out.push({
      id: `baja-${p.startId || i}`,
      tipo: 'baja',
      fechaInicio: dayKeyInZone(p.start, tz),
      fechaFin: dayKeyInZone(p.end, tz),
      estado: statusLabel(p.approvalStatus),
    }));
    absencePeriods.filter(inYear).forEach((p, i) => {
      // Día y hora de reloj en la zona del trabajador (coherentes entre sí).
      out.push({
        id: `aus-${p.startId || i}`,
        tipo: 'ausencia',
        fechaInicio: dayKeyInZone(p.start, tz),
        fechaFin: dayKeyInZone(p.end, tz),
        horaInicio: formatHmInZone(p.start, tz),
        horaFin: formatHmInZone(p.end, tz),
        estado: statusLabel(p.approvalStatus),
      });
    });
    return out.sort((a, b) => b.fechaInicio.localeCompare(a.fechaInicio));
  }, [vacationPeriods, sickPeriods, absencePeriods, tz, yearWindow]);

  const todayIso = todayKey;

  const selectionRange = useMemo(() => {
    if (!dragStart || !dragEnd) return null;
    const [start, end] = orderedRange(dragStart, dragEnd);
    return { start, end, days: datesBetween(start, end) };
  }, [dragStart, dragEnd]);

  const selectionSet = useMemo(() => new Set(selectionRange?.days || []), [selectionRange]);

  const goPrev = () => {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  };
  const goNext = () => {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  };

  const clearSelection = () => { setDragStart(null); setDragEnd(null); };

  const submitPeriodRequest = async (tipo, fechaInicio, fechaFin, file, horaInicio = null, horaFin = null) => {
    const startType = TIPO_START_EVENT[tipo];
    const endType = TIPO_END_EVENT[tipo];
    if (startType == null) return;
    // Ausencia por horas: el "rango" es el mismo día con dos horas concretas.
    // Vacaciones/baja: rango por días completos (00:00 → 23:59).
    // Fecha y hora se interpretan en la zona del TRABAJADOR, no la del
    // navegador (un portátil en GMT creaba los periodos un día antes).
    const startTs = zonedDateTimeToInstant(fechaInicio, horaInicio || '00:00', tz).toISOString();
    const endTs = horaFin
      ? zonedDateTimeToInstant(fechaInicio, horaFin, tz).toISOString()
      : zonedDateTimeToInstant(fechaFin, '23:59', tz).toISOString();
    setSubmitting(true);
    setError(null);
    try {
      await createEvent({
        workerId: user.id,
        eventType: startType,
        timestamp: startTs,
        approvalStatus: APPROVAL_STATUS.PENDING,
      });
      await createEvent({
        workerId: user.id,
        eventType: endType,
        timestamp: endTs,
        approvalStatus: APPROVAL_STATUS.PENDING,
      });
      // Justificante adjunto — SIEMPRE opcional (una falta justificada no
      // obliga a adjuntar documento). Si viene archivo, se sube.
      let uploadStatus = null; // 'ok' | 'no-company' | error message
      if (file) {
        if (!user?.companyId) {
          uploadStatus = 'no-company';
        } else {
          try {
            const periodLabel = tipo === 'baja'
              ? 'Día completo'
              : tipo === 'ausencia'
                ? 'Por horas'
                : 'Vacaciones';
            const descripcionRango = horaInicio && horaFin
              ? `${fechaInicio} ${horaInicio}–${horaFin}`
              : `${fechaInicio} → ${fechaFin}`;
            await uploadDocument({
              file,
              companyId: user.companyId,
              workplaceId: Number(user.workingSite) || 0,
              uploaderId: user.id,
              description: `Justificante ${periodLabel.toLowerCase()} ${descripcionRango}`,
            });
            uploadStatus = 'ok';
          } catch (uploadErr) {
            uploadStatus = uploadErr.message || 'error desconocido';
          }
        }
      }
      // Confirmación (toast): qué se ha pedido, fechas y que queda pendiente
      // de aprobación; más el resultado del justificante si lo había.
      const sent = FEEDBACK.periodRequest({
        kind: tipo,
        start: fechaInicio,
        end: horaFin ? fechaInicio : fechaFin,
        startTime: horaInicio,
        endTime: horaFin,
        pending: true,
      });
      if (uploadStatus === 'ok') {
        toast.success(`${sent} ${FEEDBACK.attachmentUploaded(file.name)}`);
      } else if (uploadStatus === 'no-company') {
        toast.success(sent);
        toast.warning(FEEDBACK.attachmentNoCompany);
      } else if (uploadStatus) {
        toast.success(sent);
        toast.warning(FEEDBACK.attachmentFailed(uploadStatus));
      } else {
        toast.success(sent);
      }
      await loadEvents();
      clearSelection();
    } catch (err) {
      // RequestModal ya se ha cerrado: el error viaja en el toast.
      toast.error(err, 'No se pudo enviar la solicitud.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalSubmit = async (req) => {
    await submitPeriodRequest(req.tipo, req.fechaInicio, req.fechaFin, req.file, req.horaInicio, req.horaFin);
  };

  const openSidebarAction = (tipo) => {
    setModalRange(null);
    setModalTipo(tipo);
  };

  const openRangeAction = (tipo) => {
    if (!selectionRange) return;
    setModalRange({ start: selectionRange.start, end: selectionRange.end });
    setModalTipo(tipo);
  };

  // Devuelve el periodo de vacaciones (start/end con sus event ids) que
  // contiene una fecha ISO dada, o null si la fecha no cae en ninguna
  // vacación del trabajador. Considera tanto aprobados como pendientes.
  const findVacationPeriodForDay = useCallback((iso) => {
    if (!iso) return null;
    for (const p of vacationPeriods) {
      // Solo periodos completos (con START y END). Un VAC_START huérfano
      // no es editable: faltan datos para el extremo final.
      if (!p.startEvent || !p.endEvent) continue;
      const startIso = dayKeyInZone(p.start, tz);
      const endIso = dayKeyInZone(p.end, tz);
      if (iso >= startIso && iso <= endIso) return p;
    }
    return null;
  }, [vacationPeriods, tz]);

  const onDayMouseDown = useCallback((iso) => {
    // Si el día pertenece a un periodo de vacaciones existente, en lugar
    // de iniciar un drag de selección abrimos el modal de edición. El
    // trabajador puede así corregir fechas o solicitar eliminación sin
    // perder el periodo.
    const vac = findVacationPeriodForDay(iso);
    if (vac) {
      setEditVacationPeriod(vac);
      return;
    }
    setIsDragging(true);
    setDragStart(iso);
    setDragEnd(iso);
  }, [findVacationPeriodForDay]);

  const onDayMouseEnter = useCallback((iso) => {
    if (isDragging) setDragEnd(iso);
  }, [isDragging]);

  const cellClass = (c) => {
    if (c.empty) return '';
    const base = 'relative flex items-center justify-center rounded-md text-sm font-semibold border select-none cursor-pointer transition-all';
    const isSel = selectionSet.has(c.iso);
    // El "descanso" es "no hay slot configurado ese día en el horario VIGENTE
    // EN ESA FECHA". No depende de que sea sábado/domingo. Regla única
    // compartida con la app y la comparación (isRestDayAt).
    const isRestDay = isRestDayAt(resolveSlot, c.iso);

    if (c.iso === todayIso) return `${base} bg-primary text-white border-primary shadow-sm ${isSel ? 'ring-2 ring-offset-2 ring-primary-dark' : ''}`;
    // La DECISIÓN del estado la toma resolveDayStatus (compartida con la
    // comparación de calendarios); aquí solo mapeamos estado → estilo.
    switch (resolveDayStatus(c.iso, statusSets, { holidaysSet: holidays, isRestDay })) {
      case DAY_STATUS.VACATION_APPROVED:
        return `${base} bg-accent-light text-accent-dark border-accent-light ${isSel ? 'ring-2 ring-accent' : ''}`;
      case DAY_STATUS.VACATION_PENDING:
        return `${base} bg-accent-light/40 text-accent-dark border border-dashed border-accent ${isSel ? 'ring-2 ring-accent' : ''}`;
      // Baja aprobada: rojo full + texto blanco. Es el estado más "crítico"
      // del calendario y debe contrastar claramente con vacaciones (rosa).
      case DAY_STATUS.SICK_APPROVED:
        return `${base} bg-danger text-white border-danger font-bold shadow-sm ${isSel ? 'ring-2 ring-offset-2 ring-danger' : ''}`;
      case DAY_STATUS.SICK_PENDING:
        return `${base} bg-danger/15 text-danger border border-dashed border-danger ${isSel ? 'ring-2 ring-danger' : ''}`;
      // Ausencia justificada por horas: ámbar para diferenciarse de baja (rojo)
      // y vacaciones (rosa). Pendiente con borde discontinuo igual que las demás.
      case DAY_STATUS.ABSENCE_APPROVED:
        return `${base} bg-warning/20 text-warning border-warning ${isSel ? 'ring-2 ring-warning' : ''}`;
      case DAY_STATUS.ABSENCE_PENDING:
        return `${base} bg-warning/5 text-warning border border-dashed border-warning ${isSel ? 'ring-2 ring-warning' : ''}`;
      case DAY_STATUS.COMPENSATION:
        return `${base} bg-violet-100 text-violet-800 border-violet-300 ${isSel ? 'ring-2 ring-violet-500' : ''}`;
      case DAY_STATUS.HOLIDAY:
        return `${base} bg-emerald-100 text-emerald-800 border-emerald-200 ${isSel ? 'ring-2 ring-primary' : ''}`;
      case DAY_STATUS.WORKED:
        return `${base} bg-primary-light text-primary-darker border-primary-light ${isSel ? 'ring-2 ring-primary' : ''}`;
      // Fichaje añadido (manual/corrección) pendiente de aprobación: mismo
      // color base, pero con borde discontinuo para que el trabajador vea
      // de un golpe que "todavía no está aprobado".
      case DAY_STATUS.WORKED_PENDING:
        return `${base} bg-primary-light/40 text-primary-darker border border-dashed border-primary ${isSel ? 'ring-2 ring-primary' : ''}`;
      case DAY_STATUS.REST:
        return `${base} bg-slate-50 text-ink-muted border-transparent ${isSel ? 'ring-2 ring-primary' : ''}`;
      default:
        return `${base} bg-white text-ink border-slate-200 hover:border-primary ${isSel ? 'ring-2 ring-primary border-primary' : ''}`;
    }
  };

  // `iso` es una clave de día: la formateamos en UTC para que el navegador no
  // la desplace (new Date('YYYY-MM-DD') es medianoche UTC).
  const fmtShort = (iso) => {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  };

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Calendario</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">Tu calendario personal</h1>
          {communityName && (
            <div className="text-xs text-ink-muted mt-1">Festivos de {communityName}{user?.locality ? ` · ${user.locality}` : ''}{user?.province ? ` (${user.province})` : ''}</div>
          )}
          {!user?.province && (
            <div className="text-xs text-warning mt-1">Sin provincia asignada — no se muestran festivos locales.</div>
          )}
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-ink border border-slate-200 hover:border-primary hover:text-primary bg-white transition-colors"
        >
          <Inbox size={16} /> Mis solicitudes
          <span className="inline-flex items-center justify-center w-5 h-5 text-[11px] font-bold bg-primary text-white rounded-full">
            {requestsList.length}
          </span>
        </button>
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-ink">{error.message}</div>
          </div>
        </div>
      )}

      <div className="flex-1 grid lg:grid-cols-[260px_1fr] gap-8 min-h-0">
        <aside className="flex flex-col">
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-3">Acciones</div>
          <ul className="space-y-1">
            {SIDEBAR_ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <li key={a.key}>
                  <button
                    onClick={() => a.supported && openSidebarAction(a.key)}
                    disabled={!a.supported || submitting}
                    title={a.supported ? '' : 'Esta acción aún no está soportada por el backend'}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg border border-transparent transition-colors text-left ${
                      a.supported ? 'text-ink hover:bg-slate-50 hover:border-slate-200' : 'text-ink-muted/60 cursor-not-allowed'
                    }`}
                  >
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.color} ${a.supported ? '' : 'opacity-50'}`}>
                      <Icon size={15} />
                    </span>
                    {a.label}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-auto pt-6 border-t border-slate-200">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-3">Balance</div>
            <div className="space-y-3 text-sm">
              {showVacationSummary && !vacationCountsReady ? (
                // Sin horario (cargando o sin configurar) el cómputo daría una
                // cifra falsa (antes: 184 días por arrastre sin fin): "—".
                <div className="flex justify-between" title={scheduleLoading ? 'Cargando tu horario…' : 'Sin horario configurado: no se pueden calcular los días.'}>
                  <span className="text-ink-muted">Vacaciones</span>
                  <span className="font-bold text-ink-muted tabular-nums inline-flex items-center gap-1">
                    {scheduleLoading ? <Loader2 size={12} className="animate-spin" /> : '—'}
                    <span className="font-normal"> / {vacationsTotalDays}</span>
                  </span>
                </div>
              ) : showVacationSummary ? (
                <>
                  <div className="flex justify-between" title={`Convenio «${workerAgreement.name}» · ${vacationHolidayType === 'festivos' ? 'naturales' : 'laborables'}`}>
                    <span className="text-ink-muted">Vacaciones disfrutadas</span>
                    <span className="font-bold text-accent-dark tabular-nums">
                      {vacationsTakenDays}<span className="text-ink-muted font-normal"> / {vacationsTotalDays}</span>
                    </span>
                  </div>
                  {vacationsScheduledDays > 0 && (
                    <div className="flex justify-between" title="Aprobadas pero aún no disfrutadas. Ya descuentan de tu cupo.">
                      <span className="text-ink-muted">Programadas</span>
                      <span className="font-bold text-accent-dark tabular-nums">{vacationsScheduledDays}</span>
                    </div>
                  )}
                  {vacationsPendingDays > 0 && (
                    <div className="flex justify-between">
                      <span className="text-ink-muted">Pendientes de aprobar</span>
                      <span className="font-bold text-warning tabular-nums">{vacationsPendingDays}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-ink-muted">Restantes</span>
                    <span className={`font-bold tabular-nums ${vacationsOverCap ? 'text-danger' : 'text-emerald-700'}`}>{vacationsRemainingDays}</span>
                  </div>
                  {vacationsOverCap && (
                    <div className="text-[11px] text-danger inline-flex items-start gap-1 leading-snug">
                      <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                      <span>Has superado tu cupo anual. El admin decidirá.</span>
                    </div>
                  )}
                </>
              ) : (
                // Mismo formato que la app: aprobados y, aparte, "(+N pend.)".
                // Antes se sumaban aprobados + pendientes en un solo número.
                <div className="flex justify-between">
                  <span className="text-ink-muted">Días de vacaciones</span>
                  <span className="font-bold text-accent-dark tabular-nums">
                    {vacationCountsReady ? vacationCounts.approved.count : '—'}
                    {vacationCountsReady && vacationCounts.pending.count > 0 && (
                      <span className="text-warning font-semibold text-xs"> (+{vacationCounts.pending.count} pend.)</span>
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-ink-muted">Días de ausencia (día completo)</span>
                <span className="font-bold text-danger tabular-nums">
                  {sickCountsReady ? sickCounts.approved.count : '—'}
                  {sickCountsReady && sickCounts.pending.count > 0 && (
                    <span className="text-warning font-semibold text-xs"> (+{sickCounts.pending.count} pend.)</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Días trabajados</span>
                <span className="font-bold text-primary-darker tabular-nums">{workedDaysInYear}</span>
              </div>
            </div>
          </div>
        </aside>

        <div className="flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">{viewYear}</div>
              <div className="font-display text-2xl font-bold text-ink">{MESES[viewMonth]}</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={goPrev} className="p-2 rounded-lg text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors">
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => { setViewYear(Number(todayKey.slice(0, 4))); setViewMonth(Number(todayKey.slice(5, 7)) - 1); }}
                className="px-3 py-2 rounded-lg text-xs font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors"
              >
                Hoy
              </button>
              <button onClick={goNext} className="p-2 rounded-lg text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-ink-muted">
              <Loader2 size={18} className="animate-spin text-primary" />
              <span className="text-sm">Cargando eventos…</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1.5 mb-2">
                {DOW.map((d) => (
                  <div key={d} className="text-[10px] uppercase tracking-wider text-ink-muted font-bold text-center py-1">{d}</div>
                ))}
              </div>
              <div
                className="grid grid-cols-7 gap-1.5 flex-1 min-h-0"
                style={{ gridTemplateRows: `repeat(${Math.ceil(cells.length / 7)}, minmax(0, 1fr))` }}
              >
                {cells.map((c, i) => {
                  if (c.empty) return <div key={i} className={cellClass(c)} />;
                  const shift = dayShiftCompleted(c.iso);
                  // Etiqueta extra para tooltip cuando el día tiene festivo o
                  // compensación (que ya se reflejan en el color de la celda).
                  // Festivo: con su nombre; los de empresa, con la etiqueta
                  // "(Festivo de empresa)".
                  const dayHolidays = holidaysByDate.get(c.iso);
                  const extra = compensationSet.has(c.iso)
                    ? ' · Compensación'
                    : holidays.has(c.iso)
                      ? (dayHolidays?.length ? ` · Festivo: ${describeHolidays(dayHolidays)}` : ' · Festivo')
                      : '';
                  return (
                    <div
                      key={i}
                      className={cellClass(c)}
                      onMouseDown={(e) => { e.preventDefault(); onDayMouseDown(c.iso); }}
                      onMouseEnter={() => onDayMouseEnter(c.iso)}
                      title={`${c.iso} · ${shift.summary}${extra}`}
                    >
                      <span>{c.day}</span>
                      {/* Indicador de turnos COMPLETADOS según los fichajes
                          reales del día. 'manana'/'tarde'/'partido' muestran
                          puntos ámbar/índigo. 'descanso', 'incompleto' y
                          'pendiente' no pintan nada. */}
                      {(shift.kind === 'manana' || shift.kind === 'tarde' || shift.kind === 'partido') && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex items-center gap-0.5 pointer-events-none">
                          {(shift.kind === 'manana' || shift.kind === 'partido') && (
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          )}
                          {(shift.kind === 'tarde' || shift.kind === 'partido') && (
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-ink-muted">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-primary" /> Hoy</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-primary-light" /> Trabajado</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-accent-light" /> Vacaciones</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-accent-light/40 border border-dashed border-accent" /> Pendiente</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-danger" /> Día completo</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-warning/40 border border-warning" /> Por horas</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-200" /> Festivo</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-violet-200 border border-violet-300" /> Compensación</span>
                <span className="text-ink-muted/40">·</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Mañana</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" /> Tarde</span>
                <span className="ml-auto text-ink-muted italic hidden md:inline">Haz clic o arrastra para seleccionar varios días</span>
              </div>

              {monthHolidays.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-ink-muted">
                  {monthHolidays.map((h, idx) => (
                    <span key={`${h.date}-${idx}`} className="inline-flex items-center gap-1.5">
                      <span className="tabular-nums font-semibold text-emerald-700">{Number(h.date.slice(8, 10))}</span>
                      <span className="text-ink">{h.name}</span>
                      {isCompanyHoliday(h) && (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-semibold">{COMPANY_HOLIDAY_LABEL}</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}

          {selectionRange && (
            <div className="mt-4 p-3 rounded-xl bg-ink text-white flex items-center gap-4 flex-wrap shadow-lg">
              <div className="text-sm">
                <span className="font-bold tabular-nums">{selectionRange.days.length}</span>
                {' '}día{selectionRange.days.length === 1 ? '' : 's'} seleccionado{selectionRange.days.length === 1 ? '' : 's'}
                <span className="ml-2 text-white/60 text-xs">
                  {fmtShort(selectionRange.start)} → {fmtShort(selectionRange.end)}
                </span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                {RANGE_ACTIONS
                  .filter((a) => !a.singleDayOnly || (selectionRange?.days?.length === 1))
                  .map((a) => {
                    const Icon = a.icon;
                    return (
                      <button
                        key={a.key}
                        onClick={() => openRangeAction(a.key)}
                        disabled={submitting}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${a.cls}`}
                      >
                        <Icon size={14} /> {a.label}
                      </button>
                    );
                  })}
                <button
                  onClick={clearSelection}
                  className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  title="Cancelar selección"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-ink/30 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Mis solicitudes</div>
                <div className="font-display text-lg font-bold text-ink">{requestsList.length} totales</div>
                {tzHint && <div className="text-[11px] text-ink-muted">Horas en {tzHint.toLowerCase()}</div>}
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-lg text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {requestsList.length === 0 ? (
                <div className="py-16 text-center text-sm text-ink-muted">No hay solicitudes registradas.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-ink-muted bg-slate-50">
                      <th className="text-left font-semibold py-3 px-4">Tipo</th>
                      <th className="text-left font-semibold py-3 px-4">Fechas</th>
                      <th className="text-right font-semibold py-3 px-4">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestsList.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-ink">{TIPO_LABEL[r.tipo] || r.tipo}</div>
                        </td>
                        <td className="py-3 px-4 tabular-nums text-ink">
                          {r.tipo === 'ausencia' && r.horaInicio && r.horaFin
                            ? <>{fmtShort(r.fechaInicio)} · {r.horaInicio}–{r.horaFin}</>
                            : <>{fmtShort(r.fechaInicio)}{r.fechaFin !== r.fechaInicio && <> → {fmtShort(r.fechaFin)}</>}</>
                          }
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                            r.estado === 'aprobada' ? 'bg-success/10 text-success'
                            : r.estado === 'rechazada' ? 'bg-danger/10 text-danger'
                            : 'bg-warning/10 text-warning'
                          }`}>
                            {r.estado}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <RequestModal
        open={!!modalTipo}
        tipo={modalTipo}
        initialRange={modalRange}
        timeZone={tz}
        onClose={() => { setModalTipo(null); setModalRange(null); }}
        onSubmit={handleModalSubmit}
      />

      <VacationEditModal
        open={!!editVacationPeriod}
        period={editVacationPeriod}
        timeZone={tz}
        onClose={() => setEditVacationPeriod(null)}
        onSubmitted={() => { setEditVacationPeriod(null); loadEvents(); }}
      />
    </div>
  );
};

export default CalendarioPage;
